import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { products, fontSans, hexToRgba } from './theme'

const anchorLinks = [
  { label: 'Platform', href: '#platform', accent: products[0].accent },
  { label: 'Ontext', href: '#ontext', accent: products[1].accent },
  { label: 'Sentinel', href: '#sentinel', accent: products[2].accent },
  { label: 'Kaavya AI', href: '#kaavya', accent: '#C4B5FD' },
  { label: 'Why SynKrasis', href: '#problem', accent: '#ffffff' },
  { label: 'Company', href: '#company', accent: '#ffffff' },
]

export function Nav() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        transition: 'all 0.3s ease',
        background: scrolled ? 'rgba(8, 12, 25, 0.98)' : 'transparent',
        borderBottom: scrolled
          ? '1px solid rgba(74, 144, 226, 0.12)'
          : '1px solid transparent',
      }}
    >
      <div
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '0 2rem',
          height: '70px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Logo */}
        <a href="#hero" style={{ textDecoration: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img
              src="/synkrasis_logo.png"
              alt="Synkrasis.ai"
              style={{
                height: '32px',
                width: 'auto',
                borderRadius: '6px',
              }}
            />
            <span
              style={{
                fontFamily: fontSans,
                fontWeight: 700,
                fontSize: '1.2rem',
                color: '#ffffff',
                letterSpacing: '-0.02em',
              }}
            >
              Synkrasis<span style={{ color: '#4A90E2' }}>.ai</span>
            </span>
          </div>
        </a>

        {/* Desktop Nav Links */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2rem',
          }}
          className="nav-desktop"
        >
          {anchorLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              style={{
                fontFamily: fontSans,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.65)',
                textDecoration: 'none',
                transition: 'color 0.2s ease',
                letterSpacing: '0.01em',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#ffffff')}
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = 'rgba(255,255,255,0.65)')
              }
            >
              {link.href === '#kaavya' ? (
                <img
                  src="/Kaavya_Agent.png"
                  alt=""
                  style={{
                    width: '16px',
                    height: 'auto',
                    borderRadius: '3px',
                  }}
                />
              ) : (
                <span
                  style={{
                    width: '5px',
                    height: '5px',
                    borderRadius: '50%',
                    background: link.accent,
                    boxShadow: `0 0 8px ${link.accent}`,
                    opacity: 0.9,
                  }}
                />
              )}
              {link.label}
            </a>
          ))}
        </div>

        {/* CTA Buttons */}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
          className="nav-desktop"
        >
          <a
            href="#sentinel"
            style={{
              fontFamily: fontSans,
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#2DD4BF',
              textDecoration: 'none',
              padding: '0.5rem 0.9rem',
              borderRadius: '8px',
              border: '1px solid rgba(45, 212, 191, 0.25)',
              background: 'rgba(45, 212, 191, 0.06)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(45, 212, 191, 0.5)'
              e.currentTarget.style.background = 'rgba(45, 212, 191, 0.12)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(45, 212, 191, 0.25)'
              e.currentTarget.style.background = 'rgba(45, 212, 191, 0.06)'
            }}
          >
            Try Sentinel — Free
          </a>
          <a
            href="#demo"
            style={{
              fontFamily: fontSans,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#ffffff',
              textDecoration: 'none',
              padding: '0.5rem 1.25rem',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
              transition: 'all 0.2s ease',
              boxShadow: '0 0 20px rgba(74, 144, 226, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)'
              e.currentTarget.style.boxShadow =
                '0 0 30px rgba(74, 144, 226, 0.5)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow =
                '0 0 20px rgba(74, 144, 226, 0.3)'
            }}
          >
            Get a Demo
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="nav-mobile"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#fff',
            padding: '0.5rem',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            {mobileOpen ? (
              <>
                <line
                  x1="4"
                  y1="4"
                  x2="18"
                  y2="18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="18"
                  y1="4"
                  x2="4"
                  y2="18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </>
            ) : (
              <>
                <line
                  x1="3"
                  y1="7"
                  x2="19"
                  y2="7"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="3"
                  y1="12"
                  x2="19"
                  y2="12"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="3"
                  y1="17"
                  x2="19"
                  y2="17"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              background: 'rgba(8, 12, 25, 0.99)',
              borderTop: '1px solid rgba(74, 144, 226, 0.15)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '1rem 2rem 1.5rem' }}>
              {anchorLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '0.75rem 0',
                    fontFamily: fontSans,
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.8)',
                    textDecoration: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {link.href === '#kaavya' ? (
                    <img
                      src="/Kaavya_Agent.png"
                      alt=""
                      style={{ width: '18px', height: 'auto', borderRadius: '3px' }}
                    />
                  ) : (
                    <span
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: link.accent,
                        boxShadow: `0 0 8px ${hexToRgba(link.accent, 0.8)}`,
                      }}
                    />
                  )}
                  {link.label}
                </a>
              ))}
              <div
                style={{
                  marginTop: '1rem',
                  display: 'flex',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                }}
              >
                <a
                  href="#sentinel"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: fontSans,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#2DD4BF',
                    textDecoration: 'none',
                    padding: '0.6rem 1.1rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(45, 212, 191, 0.3)',
                  }}
                >
                  Try Sentinel — Free
                </a>
                <a
                  href="#demo"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: fontSans,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#ffffff',
                    textDecoration: 'none',
                    padding: '0.6rem 1.25rem',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
                  }}
                >
                  Get a Demo
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 900px) {
          .nav-desktop { display: none !important; }
          .nav-mobile { display: block !important; }
        }
        @media (min-width: 901px) {
          .nav-mobile { display: none !important; }
        }
      `}</style>
    </motion.nav>
  )
}
